import { Pagination } from "@core/domain/models/pagination";
import { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { EditCostMapper } from "@entity/data/mappers/cost/edit-cost.mapper";
import { CostRepository } from "@entity/data/repositories/cost.repository";
import {
    CostType,
    CostTypeEnum,
} from "@entity/domain/models/cost/cost-type.model";
import { AdditionalExpense, Cost } from "@entity/domain/models/cost/cost.model";
import { GetAllCostsTypesUseCase } from "@entity/domain/usecases/cost/get-all-costs-types.usecase";
import { GetCostByIdUseCase } from "@entity/domain/usecases/cost/get-cost-by-id.usecase";
import { UpdateCostUseCase } from "@entity/domain/usecases/cost/update-cost.usecase";
import { CreateExpenseMapper } from "@project/data/mappers/expenses/create-expense.mapper";
import { EditExpenseMapper } from "@project/data/mappers/expenses/edit-expense.mapper";
import type { ExpenseSearchFilters } from "@project/domain/models/expenses/expense-search-filters";
import { ExpenseType } from "@project/domain/models/expenses/expense-type.model";
import { ExpenseAllTypes } from "@project/domain/models/expenses/expense.model";
import { Expenses } from "@project/domain/models/expenses/expenses.model";
import { CreateExpenseUseCase } from "@project/domain/usecases/expenses/create-expense.usecase";
import { DeleteExpenseUseCase } from "@project/domain/usecases/expenses/delete-expense.usecase";
import { EditExpenseUseCase } from "@project/domain/usecases/expenses/edit-expense.usecase";
import { GetAllExpensesUseCase } from "@project/domain/usecases/expenses/get-all-expenses.usecase";
import { GetAllProjectExpenseTypesUseCase } from "@project/domain/usecases/expenses/get-all-project-expense-types.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import {
    action,
    computed,
    flow,
    flowResult,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import {
    CostSummary,
    CostsSummary,
    type CostSearchFilters,
} from "../../../../../entity/domain/models/cost/cost-summary.model";
import { GetCostsByUseCase } from "../../../../../entity/domain/usecases/cost/get-costs-by.usecase";
import { EntityCostFormValuesValidated } from "./entity-cost-form/entity-cost-form.component";
import { ExpensesFormValuesValidated } from "./expenses-form/expense-modal-body.component";

const MAX_PERCENTAGE = 100;

export interface ExpenseListTable {
    id: number;
    invoiceNumber: string;
    concept: string;
    typeLabel: string;
    subTypeLabel: string;
    totalCost: number;
}
interface ExpensesListTable {
    expenses: ExpenseListTable[];
    count: number;
}

export interface ExpenseEntityListTable {
    id: number;
    invoiceId: number;
    invoiceNumber: string;
    concept: string;
    typeLabel: CostTypeEnum;
    percentage: string;
    total: number;
}

interface ExpensesEntityListTable {
    expenses: ExpenseEntityListTable[];
    count: number;
}
export interface DocumentTable {
    id: number;
    name: string;
    uploadDate: DateTime;
}

@injectable()
export class ExpensesTabViewModel extends BaseViewModel {
    projectId: Undefinable<number>;

    @observable
    expensesModalIsOpen: boolean = false;

    @observable
    linkEntityCostModalIsOpen: boolean = false;

    @observable
    initialLoading: boolean = true;

    @observable
    types: ExpenseType[] = [];

    @observable
    private _expenses: Expenses = new Expenses([], 0);

    @observable
    expenses: ExpenseAllTypes[] = [];

    @observable
    private _expensesEntity: CostsSummary = new CostsSummary([], 0);

    @observable
    expensesEntity: CostSummary[] = [];

    @observable
    expenseForm: Undefinable<ExpenseAllTypes>;

    @observable
    pagination: Pagination = new Pagination();

    initialFiltersValue: Omit<ExpenseSearchFilters, "projectId"> = {
        text: "",
        type: null,
    };

    initialExpensesFiltersValue: CostSearchFilters = {
        search: "",
        type: null,
    };

    @observable
    costTypes: CostType[] = [];

    @observable
    filters: ExpenseSearchFilters = this.initialFiltersValue;

    @observable
    entityFilters: CostSearchFilters = this.initialExpensesFiltersValue;

    @computed
    get expensesTable(): ExpensesListTable {
        return {
            count: this._expenses.count,
            expenses: this._expenses.expenses.map((expense) => ({
                id: expense.id,
                invoiceNumber:
                    "invoiceNumber" in expense && expense.invoiceNumber
                        ? expense.invoiceNumber
                        : expense.id.toString(),
                concept: expense.concept ?? "",
                typeLabel: expense.typeLabel,
                subTypeLabel:
                    "subTypeLabel" in expense ? expense.subTypeLabel : "",
                totalCost: expense.amountImputation ?? 0,
            })),
        };
    }

    @computed
    get expensesEntityTable(): ExpensesEntityListTable {
        return {
            count: this._expensesEntity.count,
            expenses: this._expensesEntity.costsSummary.map((expense) => {
                const additional = this.getAdditionalExpenseForProject(expense);
                // Backend field name in summary is total (mapped to totalCost?); ensure we fall back gracefully.
                const totalCost =
                    expense.totalCost ?? (expense as any).total ?? 0;
                const amount = additional?.amount ?? 0;
                const percentage =
                    totalCost > 0
                        ? ((amount / totalCost) * MAX_PERCENTAGE).toFixed(2)
                        : "0";

                return {
                    id: expense.id,
                    invoiceId: additional?.invoice ?? 0,
                    invoiceNumber: expense.invoiceNum,
                    concept: expense.concept,
                    typeLabel: expense.type,
                    percentage,
                    total: amount,
                };
            }),
        };
    }

    constructor(
        @inject(CreateExpenseMapper)
        private readonly createExpenseMapper: CreateExpenseMapper,
        @inject(CreateExpenseUseCase)
        private readonly createExpenseUseCase: CreateExpenseUseCase,
        @inject(EditExpenseMapper)
        private readonly editExpenseMapper: EditExpenseMapper,
        @inject(EditExpenseUseCase)
        private readonly editExpenseUseCase: EditExpenseUseCase,
        @inject(GetAllExpensesUseCase)
        private readonly getAllExpensesUseCase: GetAllExpensesUseCase,
        @inject(GetCostsByUseCase)
        private readonly getCostsByUseCase: GetCostsByUseCase,
        @inject(GetAllProjectExpenseTypesUseCase)
        private readonly getAllProjectExpenseTypesUseCase: GetAllProjectExpenseTypesUseCase,
        @inject(DeleteExpenseUseCase)
        private readonly deleteExpenseUseCase: DeleteExpenseUseCase,
        @inject(UpdateCostUseCase)
        private readonly updateCostUseCase: UpdateCostUseCase,
        @inject(CostRepository)
        private readonly costRepository: CostRepository,
        @inject(EditCostMapper)
        private readonly editCostMapper: EditCostMapper,
        @inject(GetCostByIdUseCase)
        private readonly getCostByIdUseCase: GetCostByIdUseCase,
        @inject(GetAllCostsTypesUseCase)
        private readonly getAllCostsTypesUseCase: GetAllCostsTypesUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.initViewData();
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        yield flowResult([
            this.getAllExpenses(this.pagination, this.filters),
            this.getAllEntityExpenses(this.pagination, this.entityFilters),
            this.getAllCostsTypes(),
            this.getAllExpenseTypes(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    openExpensesModal(): void {
        this.expensesModalIsOpen = true;
    }

    @action
    closeExpensesModal(): void {
        this.expenseForm = undefined;
        this.expensesModalIsOpen = false;
    }

    @action
    openLinkEntityCost(): void {
        this.linkEntityCostModalIsOpen = true;
    }

    @action
    closeLinkEntityCost(): void {
        this.linkEntityCostModalIsOpen = false;
    }

    async setFilters(filters: ExpenseSearchFilters): Promise<void> {
        this.filters = filters;
        this.pagination.reset();

        this.getAllExpenses(this.pagination, this.filters);
    }

    @action
    async setPagination(page: number, pageSize: number): Promise<void> {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        await this.getAllExpenses(this.pagination, this.filters);
        // Keep entity linked costs in sync with page changes (same pagination source for now)
        await this.getAllEntityExpenses(this.pagination, this.entityFilters);
    }

    @action
    setExpenseForm(expenseId: number): void {
        this.expenseForm = this._expenses.expenses.find(
            (expense) => expense.id === expenseId,
        );
    }

    async getAllCostsTypes(): Promise<void> {
        const costsTypes = await this.getAllCostsTypesUseCase.execute();

        runInAction(() => {
            this.costTypes = costsTypes;
        });
    }

    async getAllEntityExpenses(
        pagination: Pagination,
        filters: CostSearchFilters,
    ): Promise<void> {
        if (!this.projectId) throw new Error("Mising projectId");
        // Extend filters with projectId so datasource can pass it to backend (if supported)
        const costSummary = await this.getCostsByUseCase.execute(pagination, {
            ...filters,
            projectId: this.projectId,
        } as CostSearchFilters & { projectId: number });

        // Show only costs that have an additional expense for THIS project.
        const expensesByProject = costSummary.costsSummary.filter((cost) =>
            cost.additionalExpenses?.some(
                (ae) => ae.project === this.projectId,
            ),
        );

        runInAction(() => {
            this._expensesEntity = new CostsSummary(
                expensesByProject,
                expensesByProject.length,
            );
            this.expensesEntity = expensesByProject;
        });
    }

    async getAllExpenses(
        pagination: Pagination,
        filters: ExpenseSearchFilters,
    ): Promise<void> {
        if (!this.projectId) throw new Error("Mising projectId");
        const expensesResult = await this.getAllExpensesUseCase.execute(
            pagination,
            {
                ...filters,
                projectId: this.projectId,
            },
        );

        runInAction(() => {
            // Store full domain object for count + list
            this._expenses = expensesResult;
            this.expenses = expensesResult.expenses;
        });
    }

    async createExpense(
        createFormValues: ExpensesFormValuesValidated,
        projectId: number,
        projectFinantialEntityId: number | null,
    ): Promise<void> {
        LoadLayoutStore.start();

        await this.createExpenseUseCase.execute(
            this.createExpenseMapper.mapFromFormValues(
                createFormValues,
                projectId,
                projectFinantialEntityId,
            ),
        );

        this.getAllExpenses(this.pagination, this.filters);

        LoadLayoutStore.finish();
    }

    async editExpense(
        editFormValues: ExpensesFormValuesValidated,
        expenseId: number,
        projectId: number,
        projectFinantialEntityId: number | null,
    ): Promise<void> {
        LoadLayoutStore.start();

        await this.editExpenseUseCase.execute(
            this.editExpenseMapper.mapFromFormValues(
                editFormValues,
                expenseId,
                projectId,
                projectFinantialEntityId,
            ),
        );

        this.getAllExpenses(this.pagination, this.filters);

        LoadLayoutStore.finish();
    }

    async deleteExpense(expenseId: number): Promise<void> {
        await this.deleteExpenseUseCase.execute(expenseId);
        this.getAllExpenses(this.pagination, this.filters);
    }

    async getAllExpenseTypes(): Promise<void> {
        const types = await this.getAllProjectExpenseTypesUseCase.execute();

        runInAction(() => {
            this.types = types;
        });
    }

    async updateCost(
        values: EntityCostFormValuesValidated,
    ): Promise<Nullable<Cost>> {
        if (!values.costId) {
            throw Error("Missing costId");
        }

        const costToUpdate = await this.getCostByIdUseCase.execute(
            values.costId,
        );

        if (!costToUpdate) return null;

        LoadLayoutStore.start();

        // 1. Determine if an additional expense already exists for this project
        const existingAdditional = costToUpdate.additionalExpenses?.find(
            (ae) => ae.project === Number(values.projectId),
        );

        const amount = values.amountImputation ?? 0;
        const projectId = Number(values.projectId);

        // 2. Create or update via dedicated endpoints
        if (existingAdditional) {
            await this.costRepository.updateAdditionalExpense({
                id: existingAdditional.id,
                project: projectId,
                invoice: values.costId,
                amount,
            });
        } else {
            await this.costRepository.createAdditionalExpense({
                project: projectId,
                invoice: values.costId,
                amount,
            });
        }

        ToastManagerStore.success();
        this.linkEntityCostModalIsOpen = false;
        await this.getAllEntityExpenses(this.pagination, this.entityFilters);
        LoadLayoutStore.finish();

        return costToUpdate;
    }

    // --- Helpers ---
    private getAdditionalExpenseForProject(
        cost: CostSummary,
    ): AdditionalExpense | undefined {
        if (!this.projectId) return undefined;
        return cost.additionalExpenses?.find(
            (additionaExpense) => additionaExpense.project === this.projectId,
        );
    }
}
